<template>
  <a
    v-if="contactHref"
    :class="{ 'small-device-text': isXs }"
    :href="contactHref"
  >
    {{ " " }}{{ displayText }}{{ " " }}
    <bm-icon v-if="iconSuffix" :icon="iconSuffix" />
  </a>
  <p v-else-if="isContactMode" :class="{ 'small-device-text': isXs }">
    {{ " " }}{{ displayText }}{{ " " }}
    <bm-icon v-if="iconSuffix" :icon="iconSuffix" />
  </p>
  <a
    v-else-if="externalLink"
    :class="{ 'small-device-text': isXs }"
    :target="linkTarget"
    :href="externalLink"
  >
    {{ " " }}{{ displayText }}{{ " " }}
    <bm-icon v-if="iconSuffix" :icon="iconSuffix" />
  </a>
  <router-link
    v-else-if="location"
    :class="{
      'bm-grid-clickable': !!onClicked,
      'small-device-text': isXs,
    }"
    :to="location"
    :target="linkTarget"
    rel="noopener noreferrer"
  >
    {{ " " }}{{ displayText }}{{ " " }}
    <bm-icon v-if="iconSuffix" :icon="iconSuffix" />
  </router-link>
  <p
    v-else
    :class="{
      'bm-grid-clickable': !!onClicked,
      'small-device-text': isXs,
    }"
    v-on="onClicked ? { click: onPlainClick } : {}"
  >
    {{ " " }}{{ displayText }}{{ " " }}
    <bm-icon v-if="iconSuffix" :icon="iconSuffix" />
  </p>
</template>

<script lang="ts" setup>
import { computed } from "vue";
import type {
  Renderable,
  RendererMetadata,
} from "@/components/controls/grid/BmGridData";
import type { RawLocation } from "vue-router";
import { truncate } from "lodash";
import type { TextTruncationOptions } from "@/components/controls/grid/builder/ColumnBuilders";
import {
  prefix,
  suffix,
} from "@/components/controls/grid/renderers/rendererUtilities";
import { screenSize } from "@/vue23switching/screenSize";
import type { Maybe } from "@/components/utils/typeHelpers";
import type { BmIconName } from "@/components/controls/bmIconMap";

const nullPlaceholder = "-";

function getLocation(
  cellMetadata: RendererMetadata | undefined,
  item: unknown,
  value: Maybe<string>
): RawLocation | undefined {
  return cellMetadata?.locationExtractor?.(item, value) ?? undefined;
}

function getExternalLink(
  cellMetadata: RendererMetadata | undefined,
  item: unknown,
  value: Maybe<string>
): string | undefined {
  const link = cellMetadata?.externalLinkExtractor?.(item, value);
  if (!link) {
    return undefined;
  }

  // append http:// to the extracted url if there isn't http(s)
  // already to prevent Vue from rendering the href as
  // `https://localhost:7000/www.google.com`
  if (!/^https?:\/\//i.test(link)) {
    return "http://" + link;
  }

  return link;
}

function getIconSuffix(
  cellMetadata: RendererMetadata | undefined,
  item: unknown,
  value: Maybe<string>
): BmIconName | undefined {
  return cellMetadata?.iconSuffixExtractor?.(item, value) ?? undefined;
}

function cellContent(
  rawValue: Maybe<string>,
  context: Renderable,
  cellMetadata: RendererMetadata | undefined,
  item: unknown
) {
  const options = cellMetadata?.textTruncationOptions as
    | TextTruncationOptions
    | undefined;
  const truncateValue = options && !cellMetadata?.isAgTruncated;

  const value =
    truncateValue && rawValue != null
      ? truncate(rawValue, {
          length: options.length,
          omission: options.omission,
          separator: options.separator,
        })
      : rawValue;

  if (value && value !== nullPlaceholder) {
    const p = prefix(context, item, value);
    const s = suffix(context, item, value);
    return `${p ? p + " " : ""}${value}${s ? " " + s : ""}`;
  }
  return nullPlaceholder;
}

interface DataCellTextProps {
  item?: unknown;
  value?: Maybe<string>;
  context?: Renderable;
  onClicked?: (event: MouseEvent) => void;
}

const props = defineProps<DataCellTextProps>();

const isXs = computed(() => screenSize.xs);

const cellMetadata = computed(
  (): RendererMetadata | undefined => props.context?.rendererMetadata
);

const location = computed(() =>
  getLocation(cellMetadata.value, props.item, props.value)
);

const externalLink = computed(() =>
  getExternalLink(cellMetadata.value, props.item, props.value)
);

const iconSuffix = computed(() =>
  getIconSuffix(cellMetadata.value, props.item, props.value)
);

const displayText = computed(() =>
  cellContent(
    props.value,
    props.context as Renderable,
    cellMetadata.value,
    props.item
  )
);

const isContactMode = computed(
  () =>
    !!(cellMetadata.value?.emailExtractor || cellMetadata.value?.phoneExtractor)
);

const contactHref = computed((): string | undefined => {
  const meta = cellMetadata.value;
  if (!meta) {
    return undefined;
  }

  if (meta.emailExtractor) {
    const extractedItem = meta.emailExtractor(props.item, props.value);
    return extractedItem ? `mailto:${extractedItem}` : undefined;
  }

  if (meta.phoneExtractor) {
    const extractedItem = meta.phoneExtractor(props.item, props.value);
    return extractedItem ? `tel:${extractedItem}` : undefined;
  }

  return undefined;
});

const linkTarget = computed(() =>
  cellMetadata.value?.newTab ? "_blank" : "_self"
);

function onPlainClick(event: MouseEvent) {
  event.stopPropagation();
  // Prevents routing actions when we want to open an info-dialog
  event.preventDefault();
  props.onClicked?.(event);
}
</script>

<style scoped lang="sass">
span
  white-space: nowrap

.card-fields span.small-device-text
  white-space: normal
  overflow-wrap: break-word

a, p
  white-space: nowrap
  overflow: hidden
  text-overflow: ellipsis
</style>
